import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SurfaceCard } from "@/components/common/Cards";
import { initials, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Plus, Search, ChevronRight, Phone, Mail, LayoutGrid, List, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

const COLS = [["name", "Name"], ["age", "Age / sex"], ["phone", "Phone"], ["condition", "Condition"], ["last", "Last visit"], ["status", "Status"]];
const val = (p, k) => (k === "condition" ? (p.conditions || []).join(", ") : k === "last" ? p.last_visit || p.updated_at || p.created_at || "" : k === "age" ? Number(p.age) || 0 : p[k] || "");

function PatientTable({ rows, sort, setSort, onOpen }) {
  const sorted = [...rows].sort((a, b) => { const x = val(a, sort.k); const y = val(b, sort.k); return (x > y ? 1 : x < y ? -1 : 0) * sort.dir; });
  return (
    <SurfaceCard className="overflow-x-auto p-0" data-testid="patients-table">
      <table className="w-full min-w-[720px] text-sm">
        <thead><tr className="border-b border-[#E2E8F0] text-left text-[#8B9698] dark:border-[#223139]">{COLS.map(([k, l]) => (
          <th key={k} className="px-4 py-3 font-semibold"><button type="button" onClick={() => setSort({ k, dir: sort.k === k ? -sort.dir : 1 })} className="inline-flex items-center gap-1 hover:text-[#00A85A]" data-testid={`sort-${k}`}>{l}<ArrowUpDown className="h-3 w-3" /></button></th>
        ))}</tr></thead>
        <tbody>{sorted.map((p) => (
          <tr key={p.id} onClick={() => onOpen(p)} className="cursor-pointer border-b border-[#E2E8F0]/60 hover:bg-[#F4FBF7] dark:border-[#223139]/60 dark:hover:bg-white/5" data-testid={`patient-row-${p.id}`}>
            <td className="px-4 py-3 font-semibold text-[#101516] dark:text-white">{p.name}<div className="text-xs font-normal text-[#8B9698]">{p.patient_id}</div></td>
            <td className="px-4 py-3">{p.age ? `${p.age}y` : "—"} · {p.gender || "—"}</td>
            <td className="px-4 py-3">{p.phone || "—"}</td>
            <td className="px-4 py-3">{(p.conditions || []).join(", ") || "—"}</td>
            <td className="px-4 py-3">{val(p, "last") ? formatDate(val(p, "last")) : "—"}</td>
            <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
          </tr>
        ))}</tbody>
      </table>
    </SurfaceCard>
  );
}

export default function Patients() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [view, setViewState] = useState(() => localStorage.getItem("contaha_patients_view") || "grid");
  const [sort, setSort] = useState({ k: "name", dir: 1 });
  const setView = (v) => { setViewState(v); localStorage.setItem("contaha_patients_view", v); };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", age: "", gender: "Male", phone: "", email: "", chief_complaint: "" });

  const load = () => api.get("/patients").then((r) => setPatients(r.data)).catch(() => setPatients([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Patient name is required");
    try {
      await api.post("/patients", { ...form, age: form.age ? Number(form.age) : null });
      toast.success("Patient added");
      setOpen(false);
      setForm({ name: "", age: "", gender: "Male", phone: "", email: "", chief_complaint: "" });
      load();
    } catch { toast.error("Could not add patient"); }
  };

  const filtered = (patients || []).filter((p) => {
    const matchQ = p.name.toLowerCase().includes(q.toLowerCase()) || p.patient_id.toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all" || p.status === filter;
    return matchQ && matchF;
  });

  const AddDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-patient-btn" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">
          <Plus className="h-4 w-4 mr-1.5" /> Add Patient
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Add New Patient</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs font-semibold">Full name</Label><Input data-testid="patient-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Age</Label><Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">Gender</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs font-semibold">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs font-semibold">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs font-semibold">Chief complaint</Label><Textarea value={form.chief_complaint} onChange={(e) => setForm({ ...form, chief_complaint: e.target.value })} className="mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button onClick={create} data-testid="save-patient-btn" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg w-full">Add Patient</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div>
      <PageHeader title="Patients" subtitle="Manage your patient records and clinical journeys" breadcrumb={["Workspace", "Patients"]} actions={AddDialog} />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B9698]" />
          <Input data-testid="patient-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patients..." className="pl-9 h-10" />
        </div>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
            <TabsTrigger value="active" data-testid="filter-active">Active</TabsTrigger>
            <TabsTrigger value="inactive" data-testid="filter-inactive">Inactive</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="grid" data-testid="view-grid"><LayoutGrid className="mr-1 h-4 w-4" />Grid</TabsTrigger>
            <TabsTrigger value="list" data-testid="view-list"><List className="mr-1 h-4 w-4" />List</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {patients && filtered.length === 0 ? (
        <EmptyState icon={Users} title="No patients found" description="Add your first patient to begin managing clinical workflows." actionLabel="Add Patient" onAction={() => setOpen(true)} testId="patients-empty" />
      ) : view === "list" ? (
        <PatientTable rows={filtered} sort={sort} setSort={setSort} onOpen={(p) => navigate(`/patients/${p.id}`)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <SurfaceCard key={p.id} className="p-5 cursor-pointer transition-colors hover:border-[#00A85A]/40" data-testid={`patient-card-${p.id}`} onClick={() => navigate(`/patients/${p.id}`)}>
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E8F7F0] dark:bg-[#00A85A]/15 text-sm font-bold text-[#008F4D] dark:text-[#3FD98A]">{initials(p.name)}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-bold text-[#101516] dark:text-white truncate">{p.name}</div>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="text-xs text-[#8B9698] mt-0.5">{p.patient_id} · {p.age ? `${p.age}y` : "—"} · {p.gender}</div>
                </div>
              </div>
              <p className="mt-3 text-xs text-[#687173] dark:text-[#8B9698] line-clamp-2">{p.chief_complaint || "No chief complaint recorded."}</p>
              <div className="mt-4 flex items-center justify-between border-t border-[#E2E8F0]/60 dark:border-[#223139]/60 pt-3">
                <div className="flex items-center gap-3 text-[11px] text-[#8B9698]">
                  {p.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</span>}
                </div>
                <span className="flex items-center gap-1 text-xs font-semibold text-[#00A85A]">Open <ChevronRight className="h-3.5 w-3.5" /></span>
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}
    </div>
  );
}
